<template lang="pug">
  div(:class="['node-wrapper','rounded-sm',`${nodeType}-node-wrapper`,'border','border-gray-200','bg-white','shadow-md']"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave")
    node-toolbar(
          v-if="data.isEditable"
          :is-visible="isToolbarVisible"
          :position="Position.Top"
          @mouseenter="onMouseEnter"
          @mouseleave="onMouseLeave")
      button(type="button", @click="onEditNode")
        i.el-icon-edit
    .d-flex.flex-col
      .d-flex.flex-col.gap-y-1
        .d-flex.gap-x-2.items-center
          slot(name="icon")
          .node-title-wrapper(v-html="title")
    template(v-if="!isContainerOnly")
      handle(v-if="isHandleUsed('target-top', 'target')"
            id="target-top"
            type="target"
            :position="Position.Top"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('target-left', 'target')"
            id="target-left"
            type="target"
            :position="Position.Left"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('target-right', 'target')"
            id="target-right"
            type="target"
            :position="Position.Right"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('target-bottom', 'target')"
            id="target-bottom"
            type="target"
            :position="Position.Bottom"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('source-top', 'source')"
            id="source-top"
            type="source"
            :position="Position.Top"
            :style="isHighlighted('source-top') ? sourceHandleStyle : null"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('source-left', 'source')"
            id="source-left"
            type="source"
            :position="Position.Left"
            :style="isHighlighted('source-left') ? sourceHandleStyle : null"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('source-right', 'source')"
            id="source-right"
            type="source"
            :position="Position.Right"
            :style="isHighlighted('source-right') ? sourceHandleStyle : null"
            :connectable="isConnectable")
      handle(v-if="isHandleUsed('source-bottom', 'source')"
            id="source-bottom"
            type="source"
            :position="Position.Bottom"
            :style="isHighlighted('source-bottom') ? sourceHandleStyle : null"
            :connectable="isConnectable")
</template>

<script>
import { Handle, Position, useNode, useVueFlow } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import DOMPurify from 'dompurify';

const TOOLBAR_TIMEOUT = 150;

export default {
  name: 'DiagramBaseNode',
  components: {
    Handle,
    NodeToolbar,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    nodeType: {
      type: String,
      required: true,
    },
    isConnectable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      Position,
      nodeInfo: {id: null},
      sourceHandleStyle: {
        background: '#16a34a',
        borderColor: '#16a34a',
      },
      edges: [],
      isToolbarVisible: false,
      toolbarVisibleTimer: null,
    };
  },
  computed: {
    title() {
      return DOMPurify.sanitize(this.data.title);
    },
    nodeId() {
      return (this.nodeInfo.id || '').replace('node_', '');
    },
    isContainerOnly() {
      // Check if the node has the container-only class
      return this.nodeInfo && this.nodeInfo.class && this.nodeInfo.class.includes('node-container-only');
    },
  },
  methods: {
    isHighlighted(handleId) {
      const list = (this.data && this.data.highlightedSourceHandles) || [];
      return list.includes(handleId);
    },
    isHandleUsed(handleId, type) {
      return this.edges.some((e) =>
        type === 'source'
          ? e.source === this.nodeInfo.id && e.sourceHandle === handleId
          : e.target === this.nodeInfo.id && e.targetHandle === handleId);
    },
    onEditNode() {
      if (typeof this.data.onEditNode === 'function') {
        this.data.onEditNode(this.data.type, this.data.id, this.data.title);
      }
    },
    onMouseEnter() {
      clearTimeout(this.toolbarVisibleTimer);
      this.isToolbarVisible = true;
    },
    onMouseLeave() {
      this.toolbarVisibleTimer = setTimeout(() => {
        this.isToolbarVisible = false;
      }, TOOLBAR_TIMEOUT);
    },
  },
  mounted() {
    const node = useNode();
    this.nodeInfo = node.node;

    node.node.data = {
      ...node.node.data,
      output: [{
        name: 'output',
      }],
    };

    const { edges } = useVueFlow();
    this.edges = edges.value;

    // make reactive to changes
    this.$watch(
      () => edges.value,
      (val) => {
        this.edges = val;
      },
      { deep: true },
    );
  },
};
</script>

<style lang="scss" scoped>
.d-flex {
  display: flex;
}
.flex-col {
  flex-direction: column;
}
.gap-y-1 {
  row-gap: 0.2rem;
}
.items-center {
  align-items: center;
}
.gap-x-2 {
  -moz-column-gap: 5px;
  column-gap: 5px;
}
.node-title-wrapper {
  font-size: 9px;
  line-height: 12px;
  width: 100px;
  text-align: left;
  overflow-wrap: break-word;
  word-break: break-word;
}

</style>

<style lang="scss">
.vue-flow__node-toolbar {
  background: #28292C;
  border: 1px solid #A8A9AB;
  border-radius: 2px;
  margin-left: 26px;
  margin-top: 5px;
}
.vue-flow__node-toolbar button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
}

.rounded-sm {
  --tw-shadow: 0 4px 6px -1px rgb(0 0 0 / .1), 0 2px 4px -2px rgb(0 0 0 / .1);
  --tw-shadow-colored: 0 4px 6px -1px var(--tw-shadow-color), 0 2px 4px -2px var(--tw-shadow-color);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
  padding: 5px 5px;
}

.node-wrapper.workflow-node-wrapper,
.node-wrapper.dialog-node-wrapper,
.node-wrapper.response-node-wrapper,
.node-wrapper.command-node-wrapper {
  border-radius: 5px;
  border: 1px solid #e5e7eb;
  background: white;
}
.rounded-sm .node-icon-svg {
  width: 11px;
  height: 11px;
  color: #2c3e50;
}
.text-base {
  font-size: 12px;
  line-height: 15px;
  margin:0;
}
.node-wrapper h3{
  text-transform: initial;
}
.node-wrapper .vue-flow__handle {
  width: 4px;
  height: 4px;
  border-radius: 4px;
  background-color:#AAA;
}
.has-children-hidden .node-wrapper .vue-flow__handle.vue-flow__handle-right {
  background-color: #3da31e;
}
.text-capitalize {
  text-transform: capitalize;
}
.node-container-only {
  z-index: -1 !important;
  background-color: #FFF;
  border: 1px solid #DDD !important;
  box-shadow: none !important;
  opacity: 0.5 !important;
}
.node-container-only .vue-flow__handle {
  display: none;
}

.failure-container {
  border: 1px solid #f84b4b !important;
  background-color: #f7d9d9 !important;
}

.success-container {
  border: 1px solid #06ac50 !important;
  background-color: #b5f5ce !important;
}
</style>